import { useState, useEffect, useMemo } from 'react';
import {
  FlaskConical, Loader2, AlertTriangle, Sparkles, Play, Save, Trash2, ChevronRight,
  TrendingUp, Shuffle, GitBranch, Layers, Scale,
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import {
  getStrategyTemplates,
  quantLabParameterSweep, quantLabWalkForward, quantLabMonteCarlo,
  quantLabMultiSymbol, quantLabComparison,
  listQuantLabExperiments, saveQuantLabExperiment, deleteQuantLabExperiment,
  getExchangePublicMarkets,
  getSupportedExchanges,
} from '../services/api';
import { InfoTip } from '../components/ui';

// Tiny client-side cache of per-exchange pair lists, populated on first mount
// of a CommonInputs and reused across tool tabs. Avoids re-fetching the same
// 600-pair list every time the user switches between Sweep / Walk-Forward /
// Multi-Symbol / Compare panels (each panel renders its own CommonInputs).
const pairsCache = new Map(); // exchangeId -> string[]

// Module-level cache of the supported-exchanges list (set in the page's mount
// effect). Used by CommonInputs to render the dropdown without each panel
// having to fetch it. Falls back to the 11 known backtestable exchanges if
// the fetch hasn't completed yet (or fails) so the form is never empty.
const BACKTEST_FALLBACK = [
  { id: 'coinbase',  name: 'Coinbase' },
  { id: 'binance',   name: 'Binance' },
  { id: 'binanceus', name: 'Binance US' },
  { id: 'kraken',    name: 'Kraken' },
  { id: 'bybit',     name: 'Bybit' },
  { id: 'okx',       name: 'OKX' },
  { id: 'kucoin',    name: 'KuCoin' },
  { id: 'gateio',    name: 'Gate.io' },
  { id: 'bitget',    name: 'Bitget' },
  { id: 'htx',       name: 'HTX' },
  { id: 'cryptocom', name: 'Crypto.com' },
];
let backtestableExchanges = BACKTEST_FALLBACK;

// Small fallback list so the dropdown isn't empty during the very first fetch
// or if the public-markets endpoint is briefly unreachable. Same idea as
// SmartTradePage's FALLBACK_PAIRS.
const FALLBACK_PAIRS = [
  'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'AVAX/USDT', 'LINK/USDT',
  'BTC/USD',  'ETH/USD',  'SOL/USD',
];

const TOOLS = [
  { id: 'sweep',       label: 'Parameter Sweep',   icon: TrendingUp, desc: 'Try N values of one parameter, see which performs best.' },
  { id: 'walkforward', label: 'Walk-Forward',      icon: GitBranch,  desc: 'Optimize on past data, validate on unseen data. Detects overfitting.' },
  { id: 'montecarlo',  label: 'Monte Carlo',       icon: Shuffle,    desc: 'Permute trade order to see the realistic confidence interval on outcomes.' },
  { id: 'multisymbol', label: 'Multi-Symbol',      icon: Layers,     desc: 'Run the same strategy across many assets to detect asset-specific overfit.' },
  { id: 'comparison',  label: 'Compare',           icon: Scale,      desc: 'Side-by-side metrics + correlation for 2-4 strategies on the same window.' },
];

const DEFAULT_EXCHANGE = 'coinbase';
const DEFAULT_TIMEFRAME = '1h';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function daysAgoISO(d) { return new Date(Date.now() - d * 86400000).toISOString().slice(0, 10); }
function fmtUsd(n) { if (n == null || !Number.isFinite(Number(n))) return '—'; return `$${Number(n).toFixed(2)}`; }
function fmtPct(n) { if (n == null || !Number.isFinite(Number(n))) return '—'; return `${Number(n).toFixed(2)}%`; }
function fmtNum(n, d = 2) { if (n == null || !Number.isFinite(Number(n))) return '—'; return Number(n).toFixed(d); }

export default function QuantLabPage() {
  const [tool, setTool] = useState('sweep');
  const [templates, setTemplates] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const [templatesError, setTemplatesError] = useState(false);
  const [exchanges, setExchanges] = useState(backtestableExchanges);

  useEffect(() => {
    // Settle each request independently — a transient blip on templates
    // shouldn't blank out experiments (or vice versa) and shouldn't surface
    // as a scary page-level red banner. Inline retry hint is enough.
    const [tP, eP, xP] = [
      getStrategyTemplates().catch(() => null),
      listQuantLabExperiments().catch(() => null),
      getSupportedExchanges().catch(() => null),
    ];
    tP.then(t => {
      const tpls = (t?.data || t)?.templates || t?.data || t || [];
      const arr = Array.isArray(tpls) ? tpls : [];
      setTemplates(arr);
      setTemplatesError(arr.length === 0);
    });
    eP.then(e => setExperiments((e?.data || e)?.experiments || []));
    xP.then(x => {
      const all = (x?.exchanges || x?.data?.exchanges || []);
      // Quant Lab only cares about exchanges where the platform can pull
      // historical OHLCV. Filter to backtestSupported=true; if the backend
      // returns nothing, keep the fallback list (set at module init).
      const backtestable = all.filter(e => e.backtestSupported);
      if (backtestable.length) {
        backtestableExchanges = backtestable;
        setExchanges(backtestable);
      }
    });
  }, []);

  function retryTemplates() {
    setTemplatesError(false);
    getStrategyTemplates()
      .then(t => {
        const tpls = (t?.data || t)?.templates || t?.data || t || [];
        const arr = Array.isArray(tpls) ? tpls : [];
        setTemplates(arr);
        setTemplatesError(arr.length === 0);
      })
      .catch(() => setTemplatesError(true));
  }

  function reloadExperiments() {
    listQuantLabExperiments().then(r => setExperiments((r?.data || r)?.experiments || [])).catch(() => {});
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-emerald-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
            <FlaskConical className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Quant Lab</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">Elite · Trader's Suite</span>
            </div>
            <p className="text-xs text-emerald-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Parameter sweep · walk-forward · Monte Carlo · multi-symbol · comparison. Real OHLCV, no synthetic data, no overfitting.
              <InfoTip side="bottom">Use the 5 tools IN SEQUENCE: Sweep → Walk-Forward → Monte Carlo → Multi-Symbol → Compare. A strategy that passes all 4 validation stages earns the right to be paper-traded.</InfoTip>
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {templatesError && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>Strategy templates didn't load. The lab still works — you'll just need them to run anything.</span>
          <button onClick={retryTemplates} className="ml-auto px-2 py-1 rounded bg-amber-500/20 border border-amber-500/40 text-amber-100 hover:bg-amber-500/30 text-[11px] font-medium">
            Retry
          </button>
        </div>
      )}

      {/* Tool tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {TOOLS.map(t => {
          const Icon = t.icon;
          const active = tool === t.id;
          return (
            <button
              key={t.id}
              onClick={() => { setTool(t.id); setResult(null); }}
              className={`text-left rounded-xl border p-3 transition ${active ? 'border-emerald-500/60 bg-emerald-500/10' : 'border-dark-700 bg-dark-800/40 hover:border-dark-500'}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${active ? 'text-emerald-300' : 'text-gray-400'}`} />
                <div className="text-xs font-semibold text-white">{t.label}</div>
              </div>
              <div className="text-[10px] text-gray-500 leading-relaxed">{t.desc}</div>
            </button>
          );
        })}
      </div>

      {tool === 'sweep' &&       <SweepPanel        exchanges={exchanges} templates={templates} running={running} setRunning={setRunning} setError={setError} setResult={setResult} result={result} />}
      {tool === 'walkforward' && <WalkForwardPanel  exchanges={exchanges} templates={templates} running={running} setRunning={setRunning} setError={setError} setResult={setResult} result={result} />}
      {tool === 'montecarlo' &&  <MonteCarloPanel                          running={running} setRunning={setRunning} setError={setError} setResult={setResult} result={result} />}
      {tool === 'multisymbol' && <MultiSymbolPanel  exchanges={exchanges} templates={templates} running={running} setRunning={setRunning} setError={setError} setResult={setResult} result={result} />}
      {tool === 'comparison' &&  <ComparisonPanel   exchanges={exchanges} templates={templates} running={running} setRunning={setRunning} setError={setError} setResult={setResult} result={result} />}

      {result && (
        <SaveExperimentRow tool={tool} result={result} onSaved={reloadExperiments} />
      )}

      <ExperimentsList experiments={experiments} onDelete={async id => { await deleteQuantLabExperiment(id); reloadExperiments(); }} />
    </div>
  );
}

// ─── Shared form bits ──────────────────────────────────────────────────────

function CommonInputs({ value, onChange, showSymbol = true, exchanges = backtestableExchanges }) {
  // Per-exchange pair list, fetched on demand from the public CCXT endpoint.
  // Cached in `pairsCache` across panel mounts so switching tools doesn't
  // re-hit the network. Reset the chosen symbol if it doesn't exist on the
  // newly-selected exchange — keeps the form valid for the next submit.
  const [pairs, setPairs] = useState(() => pairsCache.get(value.exchangeId) || FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);

  useEffect(() => {
    const ex = value.exchangeId;
    if (!ex) return;
    if (pairsCache.has(ex)) {
      setPairs(pairsCache.get(ex));
      return;
    }
    let cancelled = false;
    setPairsLoading(true);
    getExchangePublicMarkets(ex)
      .then(res => {
        if (cancelled) return;
        const list = (res?.markets || res?.data?.markets || []).map(m => m.symbol).filter(Boolean);
        const sorted = list.length ? list : FALLBACK_PAIRS;
        pairsCache.set(ex, sorted);
        setPairs(sorted);
        if (showSymbol && value.symbol && !sorted.includes(value.symbol)) {
          onChange({ ...value, symbol: sorted[0] });
        }
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.exchangeId]);

  const datalistId = `pairs-${value.exchangeId}`;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Field label={`Exchange (${exchanges.length})`}>
        <select value={value.exchangeId} onChange={e => onChange({ ...value, exchangeId: e.target.value })} className="ws-input">
          {exchanges.map(x => (
            <option key={x.id} value={x.id}>{x.name}</option>
          ))}
        </select>
      </Field>
      {showSymbol && (
        <Field label={`Symbol${pairsLoading ? ' (loading…)' : ` (${pairs.length} pairs)`}`}>
          <input
            list={datalistId}
            value={value.symbol}
            onChange={e => onChange({ ...value, symbol: e.target.value.toUpperCase() })}
            placeholder="Type to search…"
            className="ws-input font-mono"
            autoComplete="off"
          />
          <datalist id={datalistId}>
            {pairs.slice(0, 500).map(p => <option key={p} value={p} />)}
          </datalist>
        </Field>
      )}
      <Field label="Timeframe">
        <select value={value.timeframe} onChange={e => onChange({ ...value, timeframe: e.target.value })} className="ws-input">
          <option value="15m">15m</option>
          <option value="30m">30m</option>
          <option value="1h">1h</option>
          <option value="4h">4h</option>
          <option value="1d">1d</option>
        </select>
      </Field>
      <Field label="Initial capital">
        <input type="number" value={value.initialCapital} onChange={e => onChange({ ...value, initialCapital: Number(e.target.value) || 10000 })} className="ws-input" />
      </Field>
      <Field label="Start date">
        <input type="date" value={value.startDate} onChange={e => onChange({ ...value, startDate: e.target.value })} className="ws-input" />
      </Field>
      <Field label="End date">
        <input type="date" value={value.endDate} onChange={e => onChange({ ...value, endDate: e.target.value })} className="ws-input" />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function TemplateSelect({ templates, value, onChange, label = 'Strategy template' }) {
  return (
    <Field label={label}>
      <select value={value} onChange={e => onChange(e.target.value)} className="ws-input">
        <option value="">— pick a template —</option>
        {templates.map(t => <option key={t.id || t.templateId} value={t.id || t.templateId}>{t.name || t.label || t.id}</option>)}
      </select>
    </Field>
  );
}

function findTemplate(templates, id) {
  return templates.find(t => (t.id || t.templateId) === id);
}

function SubmitButton({ running, label, color = 'emerald' }) {
  const cls = color === 'emerald'
    ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30'
    : 'bg-cyan-500/20 border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30';
  return (
    <button type="submit" disabled={running} className={`px-5 py-2.5 rounded-lg border text-sm font-semibold transition flex items-center gap-2 disabled:opacity-50 ${cls}`}>
      {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
      {label}
    </button>
  );
}

function ResultCard({ children, title }) {
  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5">
      {title && <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>}
      {children}
    </div>
  );
}

function KpiTable({ rows, columns }) {
  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-2xl overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-dark-900/40">
          <tr className="text-[10px] uppercase tracking-wider text-gray-500">
            {columns.map(c => <th key={c.key} className={`py-2.5 px-4 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-dark-700/40">
              {columns.map(c => (
                <td key={c.key} className={`py-2 px-4 ${c.align === 'right' ? 'text-right font-mono' : ''} ${c.color ? c.color(row) : 'text-white'}`}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tool 1: Parameter Sweep ────────────────────────────────────────────────

function SweepPanel({ exchanges, templates, running, setRunning, setError, setResult, result }) {
  const [templateId, setTemplateId] = useState('');
  const [paramPath, setParamPath] = useState('actions[1].size');
  const [paramValues, setParamValues] = useState('1,2,3,4,5,6');
  const [optimizeMetric, setOptimizeMetric] = useState('sharpeRatio');
  const [common, setCommon] = useState({
    exchangeId: DEFAULT_EXCHANGE, symbol: 'BTC/USDT', timeframe: DEFAULT_TIMEFRAME,
    startDate: daysAgoISO(180), endDate: todayISO(), initialCapital: 10000,
  });

  async function submit(e) {
    e.preventDefault();
    setError(null); setResult(null); setRunning(true);
    try {
      const tpl = findTemplate(templates, templateId);
      if (!tpl) throw new Error('Pick a strategy template');
      const values = paramValues.split(',').map(v => v.trim()).map(v => Number(v)).filter(v => Number.isFinite(v));
      if (values.length < 2) throw new Error('Provide at least 2 numeric values, comma-separated');
      const res = await quantLabParameterSweep({
        baseStrategy: tpl.strategy || tpl, paramPath, paramValues: values, optimizeMetric, ...common,
      });
      setResult({ data: res?.data || res, input: { templateId, paramPath, paramValues: values, optimizeMetric, ...common } });
    } catch (err) {
      setError(err?.message || 'Sweep failed');
    } finally { setRunning(false); }
  }

  const data = result?.data;

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <TemplateSelect templates={templates} value={templateId} onChange={setTemplateId} />
          <Field label="Parameter path">
            <input value={paramPath} onChange={e => setParamPath(e.target.value)} placeholder="actions[1].size" className="ws-input font-mono" />
          </Field>
          <Field label="Optimize for">
            <select value={optimizeMetric} onChange={e => setOptimizeMetric(e.target.value)} className="ws-input">
              <option value="sharpeRatio">Sharpe ratio</option>
              <option value="totalReturnPct">Total return %</option>
              <option value="profitFactor">Profit factor</option>
              <option value="winRate">Win rate</option>
            </select>
          </Field>
        </div>
        <Field label="Values (comma-separated, e.g. 1,2,3,4,5)">
          <input value={paramValues} onChange={e => setParamValues(e.target.value)} className="ws-input font-mono" />
        </Field>
        <CommonInputs value={common} onChange={setCommon} exchanges={exchanges} />
        <SubmitButton running={running} label="Run sweep" />
      </form>

      {data && (
        <ResultCard title={`Best ${data.optimizeMetric}: ${fmtNum(data.best?.[data.optimizeMetric], 3)} at ${data.paramPath} = ${data.best?.paramValue}`}>
          <div className="h-56 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.sweep.filter(r => r.ok)}>
                <XAxis dataKey="paramValue" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip contentStyle={{ background: '#0a0f1e', border: '1px solid #1f2937', borderRadius: 8 }} />
                <Bar dataKey={data.optimizeMetric} fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <KpiTable
            columns={[
              { key: 'paramValue', label: data.paramPath, align: 'left' },
              { key: 'totalReturnPct', label: 'Return %', align: 'right', render: r => fmtPct(r.totalReturnPct), color: r => r.totalReturnPct >= 0 ? 'text-emerald-300' : 'text-red-300' },
              { key: 'sharpeRatio', label: 'Sharpe', align: 'right', render: r => fmtNum(r.sharpeRatio) },
              { key: 'winRate', label: 'Win rate', align: 'right', render: r => fmtPct((r.winRate || 0) * 100) },
              { key: 'maxDrawdownPct', label: 'Max DD', align: 'right', render: r => fmtPct(r.maxDrawdownPct) },
              { key: 'totalTrades', label: 'Trades', align: 'right' },
              { key: 'profitFactor', label: 'PF', align: 'right', render: r => fmtNum(r.profitFactor) },
            ]}
            rows={data.sweep}
          />
        </ResultCard>
      )}
    </div>
  );
}

// ─── Tool 2: Walk-Forward ───────────────────────────────────────────────────

function WalkForwardPanel({ exchanges, templates, running, setRunning, setError, setResult, result }) {
  const [templateId, setTemplateId] = useState('');
  const [paramPath, setParamPath] = useState('actions[1].size');
  const [paramValues, setParamValues] = useState('1,2,3,4,5,6,7,8');
  const [splitPct, setSplitPct] = useState(0.7);
  const [optimizeMetric, setOptimizeMetric] = useState('sharpeRatio');
  const [common, setCommon] = useState({
    exchangeId: DEFAULT_EXCHANGE, symbol: 'BTC/USDT', timeframe: DEFAULT_TIMEFRAME,
    startDate: daysAgoISO(365), endDate: todayISO(), initialCapital: 10000,
  });

  async function submit(e) {
    e.preventDefault();
    setError(null); setResult(null); setRunning(true);
    try {
      const tpl = findTemplate(templates, templateId);
      if (!tpl) throw new Error('Pick a strategy template');
      const values = paramValues.split(',').map(v => Number(v.trim())).filter(v => Number.isFinite(v));
      const res = await quantLabWalkForward({
        baseStrategy: tpl.strategy || tpl, paramPath, paramValues: values,
        splitPct: Number(splitPct), optimizeMetric, ...common,
      });
      setResult({ data: res?.data || res, input: { templateId, paramPath, paramValues: values, splitPct, optimizeMetric, ...common } });
    } catch (err) {
      setError(err?.message || 'Walk-forward failed');
    } finally { setRunning(false); }
  }

  const data = result?.data;

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <TemplateSelect templates={templates} value={templateId} onChange={setTemplateId} />
          <Field label="Parameter path"><input value={paramPath} onChange={e => setParamPath(e.target.value)} className="ws-input font-mono" /></Field>
          <Field label="Optimize for">
            <select value={optimizeMetric} onChange={e => setOptimizeMetric(e.target.value)} className="ws-input">
              <option value="sharpeRatio">Sharpe ratio</option>
              <option value="totalReturnPct">Total return %</option>
              <option value="profitFactor">Profit factor</option>
            </select>
          </Field>
          <Field label="In-sample split (0.5 – 0.9)"><input type="number" step="0.05" min="0.5" max="0.9" value={splitPct} onChange={e => setSplitPct(Number(e.target.value))} className="ws-input" /></Field>
          <Field label="Values to sweep"><input value={paramValues} onChange={e => setParamValues(e.target.value)} className="ws-input font-mono" /></Field>
        </div>
        <CommonInputs value={common} onChange={setCommon} exchanges={exchanges} />
        <SubmitButton running={running} label="Run walk-forward" />
      </form>

      {data && (
        <ResultCard title={`Generalization: ${data.generalizationRatio == null ? 'n/a' : data.generalizationRatio.toFixed(2)} — ${data.interpretation || ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PeriodMetrics label="In-sample (optimization)" w={data.inSample?.window} m={data.inSample?.metrics} accent="cyan" />
            <PeriodMetrics label="Out-of-sample (validation)" w={data.outOfSample?.window} m={data.outOfSample?.metrics} accent="emerald" />
          </div>
          <div className="text-[11px] text-gray-500 mt-3">
            Best param: <span className="text-white font-mono">{data.paramPath} = {String(data.bestParamValue)}</span>
          </div>
        </ResultCard>
      )}
    </div>
  );
}

function PeriodMetrics({ label, w, m, accent = 'emerald' }) {
  if (!m) return null;
  const cls = accent === 'cyan' ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-emerald-500/30 bg-emerald-500/5';
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <div className="text-xs font-semibold text-white">{label}</div>
      <div className="text-[10px] text-gray-500 font-mono mb-3">{w?.startDate} → {w?.endDate}</div>
      <div className="space-y-1.5 text-xs">
        <KV label="Total return" v={fmtPct(m.totalReturnPct)} color={m.totalReturnPct >= 0 ? 'text-emerald-300' : 'text-red-300'} />
        <KV label="Sharpe" v={fmtNum(m.sharpeRatio)} />
        <KV label="Max drawdown" v={fmtPct(m.maxDrawdownPct)} />
        <KV label="Win rate" v={fmtPct((m.winRate || 0) * 100)} />
        <KV label="Trades" v={m.totalTrades} />
        <KV label="Profit factor" v={fmtNum(m.profitFactor)} />
      </div>
    </div>
  );
}

function KV({ label, v, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400">{label}</span>
      <span className={`font-mono ${color || 'text-white'}`}>{v}</span>
    </div>
  );
}

// ─── Tool 3: Monte Carlo ────────────────────────────────────────────────────

function MonteCarloPanel({ running, setRunning, setError, setResult, result }) {
  const [tradesJson, setTradesJson] = useState('');
  const [initialCapital, setInitialCapital] = useState(10000);
  const [runs, setRuns] = useState(1000);

  async function submit(e) {
    e.preventDefault();
    setError(null); setResult(null); setRunning(true);
    try {
      const trades = JSON.parse(tradesJson);
      if (!Array.isArray(trades)) throw new Error('Paste the `trades` array from a backtest result (must be a JSON array).');
      const res = await quantLabMonteCarlo({ trades, initialCapital: Number(initialCapital), runs: Number(runs) });
      setResult({ data: res?.data || res, input: { initialCapital, runs, tradeCount: trades.length } });
    } catch (err) {
      setError(err?.message || 'Monte Carlo failed');
    } finally { setRunning(false); }
  }

  const data = result?.data;

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5 space-y-4">
        <div className="text-[11px] text-gray-400">
          Paste the <span className="text-white font-mono">trades</span> array from a backtest result. The tool shuffles the trade order N times and reports the distribution of outcomes — tells you whether your strategy is robust or just got lucky on one order.
        </div>
        <Field label="Trades JSON">
          <textarea
            value={tradesJson} onChange={e => setTradesJson(e.target.value)}
            rows={6} placeholder='[{ "side": "sell", "pnlUsd": 12.34, ... }, ...]'
            className="ws-input font-mono text-[11px]"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Initial capital"><input type="number" value={initialCapital} onChange={e => setInitialCapital(Number(e.target.value) || 10000)} className="ws-input" /></Field>
          <Field label="Runs (100–5000)"><input type="number" value={runs} onChange={e => setRuns(Number(e.target.value) || 1000)} className="ws-input" min="100" max="5000" /></Field>
        </div>
        <SubmitButton running={running} label="Run Monte Carlo" />
      </form>

      {data && (
        <ResultCard title={`Monte Carlo — ${data.runs} runs, ${data.trades} trades · ${(data.profitProbability * 100).toFixed(1)}% profit probability`}>
          <div className="text-xs text-emerald-200 mb-3">{data.interpretation}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <MCColumn title="Final return %" v={data.finalReturnPct} formatter={fmtPct} />
            <MCColumn title="Final equity" v={data.finalEquity} formatter={fmtUsd} />
            <MCColumn title="Max drawdown %" v={data.maxDrawdownPct} formatter={fmtPct} />
          </div>
        </ResultCard>
      )}
    </div>
  );
}

function MCColumn({ title, v, formatter }) {
  return (
    <div className="bg-dark-900/50 border border-dark-700 rounded-lg p-3 space-y-1 text-xs">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono mb-2">{title}</div>
      <KV label="5th pct" v={formatter(v.p05)} color="text-red-300" />
      <KV label="25th pct" v={formatter(v.p25)} />
      <KV label="Median" v={formatter(v.p50)} color="text-white" />
      <KV label="75th pct" v={formatter(v.p75)} />
      <KV label="95th pct" v={formatter(v.p95)} color="text-emerald-300" />
    </div>
  );
}

// ─── Tool 4: Multi-Symbol ───────────────────────────────────────────────────

function MultiSymbolPanel({ exchanges, templates, running, setRunning, setError, setResult, result }) {
  const [templateId, setTemplateId] = useState('');
  const [symbols, setSymbols] = useState('BTC/USDT,ETH/USDT,SOL/USDT,AVAX/USDT,LINK/USDT');
  const [common, setCommon] = useState({
    exchangeId: DEFAULT_EXCHANGE, symbol: '', timeframe: DEFAULT_TIMEFRAME,
    startDate: daysAgoISO(180), endDate: todayISO(), initialCapital: 10000,
  });

  async function submit(e) {
    e.preventDefault();
    setError(null); setResult(null); setRunning(true);
    try {
      const tpl = findTemplate(templates, templateId);
      if (!tpl) throw new Error('Pick a strategy template');
      const syms = symbols.split(',').map(s => s.trim()).filter(Boolean);
      if (syms.length < 2) throw new Error('Provide at least 2 symbols');
      const res = await quantLabMultiSymbol({
        strategy: tpl.strategy || tpl, symbols: syms, ...common,
      });
      setResult({ data: res?.data || res, input: { templateId, symbols: syms, ...common } });
    } catch (err) {
      setError(err?.message || 'Multi-symbol failed');
    } finally { setRunning(false); }
  }

  const data = result?.data;

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TemplateSelect templates={templates} value={templateId} onChange={setTemplateId} />
          <Field label="Symbols (comma-separated, max 10)"><input value={symbols} onChange={e => setSymbols(e.target.value)} className="ws-input font-mono text-[11px]" /></Field>
        </div>
        <CommonInputs value={common} onChange={setCommon} exchanges={exchanges} showSymbol={false} />
        <SubmitButton running={running} label="Run across symbols" />
      </form>

      {data?.aggregate && (
        <ResultCard title={`Aggregate: ${data.aggregate.profitableSymbols}/${data.aggregate.symbolCount} symbols profitable · avg return ${fmtPct(data.aggregate.avgReturnPct)} · avg Sharpe ${fmtNum(data.aggregate.avgSharpe)}`}>
          <KpiTable
            columns={[
              { key: 'symbol', label: 'Symbol' },
              { key: 'totalReturnPct', label: 'Return %', align: 'right', render: r => fmtPct(r.totalReturnPct), color: r => r.totalReturnPct >= 0 ? 'text-emerald-300' : 'text-red-300' },
              { key: 'sharpeRatio', label: 'Sharpe', align: 'right', render: r => fmtNum(r.sharpeRatio) },
              { key: 'maxDrawdownPct', label: 'Max DD', align: 'right', render: r => fmtPct(r.maxDrawdownPct) },
              { key: 'winRate', label: 'Win rate', align: 'right', render: r => fmtPct((r.winRate || 0) * 100) },
              { key: 'totalTrades', label: 'Trades', align: 'right' },
            ]}
            rows={data.perSymbol.filter(r => r.ok)}
          />
        </ResultCard>
      )}
    </div>
  );
}

// ─── Tool 5: Strategy Comparison ────────────────────────────────────────────

function ComparisonPanel({ exchanges, templates, running, setRunning, setError, setResult, result }) {
  const [picks, setPicks] = useState(['', '', '', '']);
  const [common, setCommon] = useState({
    exchangeId: DEFAULT_EXCHANGE, symbol: 'BTC/USDT', timeframe: DEFAULT_TIMEFRAME,
    startDate: daysAgoISO(180), endDate: todayISO(), initialCapital: 10000,
  });

  async function submit(e) {
    e.preventDefault();
    setError(null); setResult(null); setRunning(true);
    try {
      const selected = picks.filter(Boolean).map(id => {
        const tpl = findTemplate(templates, id);
        return { name: tpl?.name || tpl?.label || id, strategy: tpl?.strategy || tpl };
      });
      if (selected.length < 2) throw new Error('Pick at least 2 strategies');
      const res = await quantLabComparison({
        strategies: selected, ...common,
      });
      setResult({ data: res?.data || res, input: { picks: selected.map(s => s.name), ...common } });
    } catch (err) {
      setError(err?.message || 'Comparison failed');
    } finally { setRunning(false); }
  }

  const data = result?.data;
  const equityChartData = useMemo(() => {
    if (!data?.perStrategy) return null;
    const ok = data.perStrategy.filter(p => p.ok);
    if (!ok.length) return null;
    const len = Math.min(...ok.map(p => p.equityCurve.length));
    const out = [];
    for (let i = 0; i < len; i++) {
      const point = { ts: ok[0].equityCurve[i].ts };
      ok.forEach(p => { point[p.name] = p.equityCurve[i].equity; });
      out.push(point);
    }
    return out;
  }, [data]);

  const COLORS = ['#10b981', '#06b6d4', '#a78bfa', '#f59e0b'];

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map(i => (
            <TemplateSelect key={i} templates={templates} value={picks[i]} onChange={v => setPicks(p => { const n = [...p]; n[i] = v; return n; })} label={`Strategy ${i + 1}${i < 2 ? ' (required)' : ' (optional)'}`} />
          ))}
        </div>
        <CommonInputs value={common} onChange={setCommon} exchanges={exchanges} />
        <SubmitButton running={running} label="Compare strategies" />
      </form>

      {data && (
        <>
          <ResultCard title="Equity curves">
            {equityChartData ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={equityChartData}>
                    <XAxis dataKey="ts" hide />
                    <YAxis stroke="#9ca3af" fontSize={11} />
                    <Tooltip contentStyle={{ background: '#0a0f1e', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }} />
                    <Legend />
                    {data.strategyNames.map((n, i) => (
                      <Line key={n} type="monotone" dataKey={n} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="text-xs text-gray-400">No data to chart.</div>}
          </ResultCard>

          <ResultCard title="Side-by-side metrics">
            <KpiTable
              columns={[
                { key: 'name', label: 'Strategy' },
                { key: 'r', label: 'Return %', align: 'right', render: r => fmtPct(r.summary?.totalReturnPct), color: r => (r.summary?.totalReturnPct || 0) >= 0 ? 'text-emerald-300' : 'text-red-300' },
                { key: 's', label: 'Sharpe', align: 'right', render: r => fmtNum(r.summary?.sharpeRatio) },
                { key: 'dd', label: 'Max DD', align: 'right', render: r => fmtPct(r.summary?.maxDrawdownPct) },
                { key: 'wr', label: 'Win rate', align: 'right', render: r => fmtPct((r.summary?.winRate || 0) * 100) },
                { key: 't', label: 'Trades', align: 'right', render: r => r.summary?.totalTrades ?? '—' },
                { key: 'pf', label: 'PF', align: 'right', render: r => fmtNum(r.summary?.profitFactor) },
              ]}
              rows={data.perStrategy.filter(r => r.ok)}
            />
          </ResultCard>

          {Array.isArray(data.correlationMatrix) && data.correlationMatrix.length >= 2 && (
            <ResultCard title="Return correlation matrix (lower is better diversification)">
              <div className="overflow-x-auto">
                <table className="text-xs">
                  <thead>
                    <tr><th></th>{data.strategyNames.map(n => <th key={n} className="px-3 py-1 text-gray-400">{n}</th>)}</tr>
                  </thead>
                  <tbody>
                    {data.correlationMatrix.map((row, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1 text-gray-400 font-medium">{data.strategyNames[i]}</td>
                        {row.map((v, j) => (
                          <td key={j} className={`px-3 py-1 text-center font-mono ${Math.abs(v) > 0.7 ? 'text-red-300' : Math.abs(v) > 0.4 ? 'text-amber-300' : 'text-emerald-300'}`}>{v.toFixed(2)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ResultCard>
          )}
        </>
      )}
    </div>
  );
}

// ─── Save / list / delete experiments ──────────────────────────────────────

function SaveExperimentRow({ tool, result, onSaved }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);

  async function save() {
    if (!name.trim()) { setErr('Give the experiment a name'); return; }
    setSaving(true); setErr(null);
    try {
      await saveQuantLabExperiment({ name: name.trim(), tool, input: result.input, result: result.data });
      setSaved(true); setName('');
      onSaved();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setErr(e?.message || 'Save failed'); } finally { setSaving(false); }
  }

  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 flex flex-wrap items-center gap-3">
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Name this experiment..." className="ws-input flex-1 min-w-[200px]" />
      <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 text-sm font-medium transition flex items-center gap-2 disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save experiment
      </button>
      {saved && <span className="text-xs text-emerald-300">Saved.</span>}
      {err && <span className="text-xs text-red-300">{err}</span>}
    </div>
  );
}

function ExperimentsList({ experiments, onDelete }) {
  if (!experiments?.length) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-white mb-3">Saved experiments ({experiments.length})</h3>
      <div className="bg-dark-800/60 border border-dark-700 rounded-2xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-dark-900/40">
            <tr className="text-[10px] uppercase tracking-wider text-gray-500">
              <th className="text-left py-2.5 px-4 font-medium">Saved</th>
              <th className="text-left py-2.5 px-4 font-medium">Name</th>
              <th className="text-left py-2.5 px-4 font-medium">Tool</th>
              <th className="text-right py-2.5 px-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {experiments.map(e => (
              <tr key={e.experimentId} className="border-t border-dark-700/40">
                <td className="py-2.5 px-4 text-gray-400">{new Date(e.createdAt).toLocaleString()}</td>
                <td className="py-2.5 px-4 text-white">{e.name}</td>
                <td className="py-2.5 px-4 text-gray-300 font-mono text-[11px]">{e.tool}</td>
                <td className="py-2.5 px-4 text-right">
                  <button onClick={() => { if (window.confirm('Delete this experiment?')) onDelete(e.experimentId); }} className="p-1.5 rounded bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
